import { useState, useEffect, useRef } from 'react';

import PropTypes from 'prop-types';

import Slot from '@zenbusiness/zui/Slot';
import { zuiPropTypes } from '@zenbusiness/zui/utils/propTypes';
import { useSlotProps } from '@zenbusiness/zui/utils/useSlotProps';
import { useStableSlots } from '@zenbusiness/zui/utils/useStableSlots';

import { RotatingTextRoot, RotatingTextText } from './RotatingText.style';

const DEFAULT_ANIMATION_DURATION = 300;
const DEFAULT_DURATION = 3000;

function RotatingText({
  animationDuration = DEFAULT_ANIMATION_DURATION,
  duration = DEFAULT_DURATION,
  loop,
  onComplete,
  slotProps: inSlotProps,
  slots: inSlots,
  textOptions = []
}) {
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const textOptionsCount = textOptions.length;

  // Setup the slots
  const { slots, slotProps } = useStableSlots(inSlots, inSlotProps);

  const Root = slots.root ?? RotatingTextRoot;
  const rootProps = useSlotProps(Root, slotProps.root, {
    propsAdded: { animationDuration, isVisible }
  });

  const Text = slots.text ?? RotatingTextText;
  const textProps = useSlotProps(Text, slotProps.text);

  useEffect(() => {
    if (textOptionsCount) {
      intervalRef.current = setInterval(() => {
        // Calculate what the next index would be
        const nextIndex = (currentIndex + 1) % textOptionsCount;

        // Check if this is the last item and we're not looping
        if (nextIndex === 0 && !loop) {
          onComplete?.();
          clearInterval(intervalRef.current);
          return;
        }

        // Fade out
        setIsVisible(false);

        // Change text after fade out
        timeoutRef.current = setTimeout(() => {
          setCurrentIndex(nextIndex);
          setIsVisible(true);
        }, animationDuration);
      }, duration);
    }

    return () => {
      clearInterval(intervalRef.current);
      clearTimeout(timeoutRef.current);
    };
  }, [animationDuration, duration, textOptionsCount, onComplete, loop, currentIndex]);

  return (
    <Slot component={Root} props={rootProps}>
      <Slot component={Text} props={textProps}>
        {textOptions[currentIndex]}
      </Slot>
    </Slot>
  );
}

RotatingText.propTypes = {
  animationDuration: PropTypes.number,
  duration: PropTypes.number,
  onComplete: PropTypes.func,
  loop: PropTypes.bool,
  slotProps: PropTypes.shape({
    root: zuiPropTypes.slotPropType,
    text: zuiPropTypes.slotPropType
  }),
  slots: PropTypes.shape({
    root: zuiPropTypes.slotType,
    text: zuiPropTypes.slotType
  }),
  textOptions: PropTypes.arrayOf(PropTypes.node).isRequired
};

export default RotatingText;
